import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  CommandService,
  GatewayRtkConfig,
  GatewaySiteAssignmentRequest,
  GraphResponse,
  GraphService,
  IntegratorGateway,
  IntegratorGatewayService,
  PagedIntegratorGatewaysResponse,
  ReadFileRequest,
  RtkBase,
  RtkService,
  SiteService as GatewaySiteService,
  UpdateGatewayRtkRequest,
} from '@wheere/api-gateway';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { GATEWAYMANAGER_BASE_PATH } from './gatewaymanager-base-path.token';
import { errorMessage } from '@wheere-front/shared-util';

export interface GatewaysListQuery {
  search?: string;
  status?: string;
  siteId?: string;
  organizationId?: string;
  envs?: string[];
  page?: number;
  size?: number;
  sort?: string;
}

/**
 * V7 integrator-side store for gateway reads + integrator-allowed writes.
 *
 * <p>Same shape as {@code TrackersV7Store}: thin Angular signals wrapper around the generated
 * {@code @wheere/api-gateway} client. Carries only what the integrator UI needs — listing,
 * detail, health, settings (read-only), RTK status, logs (live via WS), site-assignment,
 * read-file command. The admin-only paths (firmware OTA, RF toggle, debug, fake-device,
 * config PATCH, transfer-organization) stay in {@code inside-gateways}.
 */
@Injectable({ providedIn: 'root' })
export class GatewaysV7Store {
  // ─────────────────────── Signals (state)

  readonly gateways = signal<IntegratorGateway[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly selectedGateway = signal<IntegratorGateway | null>(null);
  readonly rtk = signal<GatewayRtkConfig | null>(null);
  readonly lastError = signal<string | null>(null);

  /**
   * Live log buffer fed by the page-level WebSocket subscription on
   * {@code /topic/gateway/{id}/logs}. Owned by the page (not the {@code gateway-logs-live}
   * component) so logs keep accumulating even when the user is on another tab.
   */
  readonly liveLogs = signal<string[]>([]);

  /** Live file-content buffer (read-file response stream). */
  readonly liveFileContent = signal<string[]>([]);

  /** Live read-file status (last frame from {@code /topic/gateway/{id}/file-status}). */
  readonly fileStatus = signal<string | null>(null);

  /** Cap the live buffers — protects the browser when a gateway enters debug-spam mode. */
  private readonly MAX_LIVE_LOGS = 10_000;

  readonly hasGateways = computed(() => this.gateways().length > 0);

  // ─────────────────────── Deps

  #integratorGatewayService = inject(IntegratorGatewayService);
  #rtkService = inject(RtkService);
  #commandService = inject(CommandService);
  #siteService = inject(GatewaySiteService);
  #graphService = inject(GraphService);
  #http = inject(HttpClient);
  #basePath = inject(GATEWAYMANAGER_BASE_PATH);

  // ─────────────────────── Reads

  async loadGateways(query: GatewaysListQuery = {}): Promise<void> {
    this.loading.set(true);
    try {
      const response = await this.#fetchIntegratorGateways(query);
      this.gateways.set(response.content ?? []);
      this.total.set(response.totalElements ?? 0);
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Nombre de gateways correspondant à la requête (page de 1, `totalElements`) — compteurs de la
   * liste integrator. Ne touche pas à {@link gateways} ni à {@link total}.
   */
  async countGateways(query: Omit<GatewaysListQuery, 'page' | 'size' | 'sort'> = {}): Promise<number> {
    const response = await this.#fetchIntegratorGateways({ ...query, page: 0, size: 1 });
    return response.totalElements ?? 0;
  }

  #fetchIntegratorGateways(query: GatewaysListQuery): Promise<PagedIntegratorGatewaysResponse> {
    return lastValueFrom(
      this.#integratorGatewayService.getIntegratorGateways(
        query.siteId,
        query.organizationId,
        query.search,
        query.status,
        query.page ?? 0,
        query.size ?? 20,
        query.sort,
        query.envs
      )
    );
  }

  async loadGatewayById(id: string): Promise<void> {
    const gateway = await lastValueFrom(this.#integratorGatewayService.getIntegratorGatewayById(id));
    this.selectedGateway.set(gateway);
  }

  async loadRtk(id: string): Promise<void> {
    try {
      const config = await lastValueFrom(this.#rtkService.getGatewayRtk(id));
      this.rtk.set(config);
    } catch {
      this.rtk.set(null);
    }
  }

  /**
   * Health metrics endpoint isn't part of the generated client yet — use the raw
   * gatewaymanager URL. Returns the raw payload (uptime + statusHistory[]) which the
   * {@code health-card} component projects.
   */
  async loadHealthSnapshot(id: string): Promise<unknown> {
    return firstValueFrom(this.#http.get<unknown>(`${this.#basePath}/gateways/${id}/health`));
  }

  /**
   * Settings read-only — Gateway model already carries {@code config} (signal/attenuator/clock).
   * The {@code settings-readonly} component reads it from {@code selectedGateway} directly,
   * so this method is a no-op alias to {@link #loadGatewayById}.
   */
  async loadSettings(id: string): Promise<void> {
    return this.loadGatewayById(id);
  }

  /**
   * Pull the gateway's time-series graphs (CPU temp, memory, frequency lock, etc.) over a
   * date range. {@code reduce} is the target number of points — 0 means "no reduction"
   * (raw points). The same endpoint backs the inside Health-monitoring tab.
   */
  async loadGatewayGraphs(id: string, startDate: Date, endDate: Date, reduce = 0): Promise<GraphResponse> {
    return lastValueFrom(this.#graphService.getGatewayGraphData(id, startDate, endDate, reduce));
  }

  /** RTK bases (centipede stations) — used by the RTK map to render markers. */
  async loadGatewayRtkBases(id: string): Promise<RtkBase[]> {
    return lastValueFrom(this.#rtkService.getGatewayRtkBases(id));
  }

  /** Persist the new RTK config (mode + optional baseId). Returns the updated config. */
  async updateGatewayRtk(id: string, request: UpdateGatewayRtkRequest): Promise<GatewayRtkConfig> {
    const updated = await lastValueFrom(this.#rtkService.updateGatewayRtk(id, request));
    this.rtk.set(updated);
    return updated;
  }

  // ─────────────────────── Writes

  async assignToSite(gatewayId: string, siteId: string | null): Promise<void> {
    this.lastError.set(null);
    try {
      const body: GatewaySiteAssignmentRequest = { siteId: siteId ?? undefined };
      await lastValueFrom(this.#siteService.assignToSite(gatewayId, body));
      await this.loadGatewayById(gatewayId);
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, 'Failed to assign gateway to site'));
      throw e;
    }
  }

  /**
   * "Read file" — sends the read-file command to the gateway. Filename is the relative path on
   * the device's filesystem; {@code partition} narrows the lookup (default {@code log} like
   * inside). ORG_OPERATOR+ / PLATFORM_ADMIN.
   */
  async readFile(gatewayId: string, filename: string, partition = 'log'): Promise<void> {
    this.lastError.set(null);
    try {
      const body: ReadFileRequest = { filename, partition, last: -1 } as ReadFileRequest;
      await lastValueFrom(this.#commandService.readFile(gatewayId, body));
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, 'Failed to send read-file command'));
      throw e;
    }
  }

  /**
   * Toggle the gateway's RF emitter. The backend `disableRf` command flips the state and
   * returns the new {@code isEmitting} value — we mirror it back into the local signals so
   * the UI reflects the new state immediately, before the WS broadcast comes in.
   * Permission: {@code gateway:command:rf-toggle} (ORG_OPERATOR+ / PLATFORM_ADMIN).
   */
  async toggleRf(gatewayId: string): Promise<boolean> {
    this.lastError.set(null);
    try {
      const newState = await lastValueFrom(this.#commandService.disableRf(gatewayId));
      // Patch the in-memory caches so the toggle visualisation flips without a full reload.
      const sel = this.selectedGateway();
      if (sel?.id === gatewayId) {
        this.selectedGateway.set({ ...sel, isEmitting: newState });
      }
      this.gateways.set(this.gateways().map(g => (g.id === gatewayId ? { ...g, isEmitting: newState } : g)));
      return newState;
    } catch (e: unknown) {
      this.lastError.set(errorMessage(e, 'Failed to toggle RF'));
      throw e;
    }
  }

  // ─────────────────────── Live patches (fed by WebSocket subscriptions in the page)

  /**
   * Spread {@code changes} onto the currently-selected gateway. No-op when nothing is
   * selected — same race-window protection as TrackersV7Store#patchSelectedTracker.
   */
  patchSelectedGateway(changes: Partial<IntegratorGateway>): void {
    const current = this.selectedGateway();
    if (!current) return;
    this.selectedGateway.set({ ...current, ...changes });
  }

  /**
   * Update one entry of {@code selectedGateway.config} (one of {@code stateSignal} /
   * {@code stateAttenuator} / {@code stateClock} / {@code stateClockType} / {@code pg}).
   *
   * <p>The gateway publishes state messages on {@code /topic/gateway/{id}/state} as JSON
   * {@code { config: 'stateSignal', data: {...} }}. The page subscription routes the value
   * here so the chip strip reacts in real-time without a full reload.
   */
  patchGatewayConfigState(parameter: string, data: unknown): void {
    const current = this.selectedGateway();
    if (!current) return;
    const nextConfig = {
      ...(current.config ?? {}),
      [parameter]: typeof data === 'string' ? data : JSON.stringify(data),
    };
    this.selectedGateway.set({ ...current, config: nextConfig });
  }

  appendLiveLogs(lines: string[]): void {
    if (!lines.length) return;
    const merged = [...this.liveLogs(), ...lines];
    this.liveLogs.set(merged.length > this.MAX_LIVE_LOGS ? merged.slice(-this.MAX_LIVE_LOGS) : merged);
  }

  clearLiveLogs(): void {
    this.liveLogs.set([]);
  }

  appendFileContent(lines: string[]): void {
    if (!lines.length) return;
    const merged = [...this.liveFileContent(), ...lines];
    this.liveFileContent.set(merged.length > this.MAX_LIVE_LOGS ? merged.slice(-this.MAX_LIVE_LOGS) : merged);
  }

  clearFileContent(): void {
    this.liveFileContent.set([]);
  }

  setFileStatus(status: string | null): void {
    this.fileStatus.set(status);
  }

  // ─────────────────────── Reset

  resetSelected(): void {
    this.selectedGateway.set(null);
    this.rtk.set(null);
    this.liveLogs.set([]);
    this.liveFileContent.set([]);
    this.fileStatus.set(null);
    this.lastError.set(null);
  }
}
